import { pick, isNull, padStart } from "lodash";
import dayjs from "dayjs";
import {
  prettyNumericRound,
  getFormattedNumber,
  singleValue,
  SUB_TYPE,
  getFootageValue,
  isDropShipItem,
} from "../../utils";
import storeSuppliesItemMap from "../../pages/StoreSupplies/SSItem/itemMappings";
import { isItemDepartment } from "../../utils/deptValidator";

const getNoteValue = (list, id) => {
  const matchedNote = list.find((note) => String(note.NotesId) === String(id));
  return matchedNote?.Notes ?? "-";
};

const getValueMapper =
  (
    fields = [],
    store = {},
    plannerNotes = [],
    retailerNotes = [],
    isDashboard = false,
    type = ""
  ) =>
  (row = {}) => {
    const isXDept = isItemDepartment(row);
    let _row = pick(row, fields);

    if (fields.includes("Store") && !isDashboard) {
      _row = {
        ..._row,
        Store: !!store?.memberNumber ? store?.memberNumber : "",
      };
    }
    if (fields.includes("StoreName") && !isDashboard) {
      _row = {
        ..._row,
        StoreName: !!store?.name ? store?.name : "",
      };
    }
    if (fields.includes("MemberNumber")) {
      _row = {
        ..._row,
        MemberNumber: isNull(row?.CheckDigit)
          ? row?.MemberNumber
          : `${row?.MemberNumber}-${row?.CheckDigit}`,
      };
    }
    if (fields.includes("SelectedPogs")) {
      _row = {
        ..._row,
        SelectedPogs: `${row?.SelectedPogs || 0} of ${row?.TotalPogs || 0}`,
      };
    }
    if (fields.includes("ExistingPogs")) {
      _row = {
        ..._row,
        ExistingPogs: isXDept ? "-" : row?.ExistingPogs || 0,
      };
    }
    if (fields.includes("SkuQty")) {
      _row = {
        ..._row,
        SkuQty: getFormattedNumber(row?.SkuQty || 0),
      };
    }
    if (fields.includes("LinealFootage")) {
      _row = {
        ..._row,
        LinealFootage: isXDept ? "-" : getFootageValue(row?.LinealFootage),
      };
    }
    if (fields.includes("AdjLineal")) {
      const adjLfValue =
        row?.AllItemsFromFillIn || isXDept
          ? "-"
          : isNull(row?.AdjLineal)
          ? getFootageValue(row?.LinealFootage)
          : getFootageValue(row?.AdjLineal);
      _row = {
        ..._row,
        AdjLineal: adjLfValue,
      };
    }
    if (fields.includes("AdjCost")) {
      const costValue = prettyNumericRound(row?.AdjCost || 0);
      _row = {
        ..._row,
        AdjCost: row?.AllItemsFromFillIn
          ? "-"
          : `$${getFormattedNumber(costValue)}`,
      };
    }
    if (fields.includes("adjCost") && row?.IsFromFillIn) {
      _row = {
        ..._row,
        adjCost: "-",
      };
    }
    if (fields.includes("ExtCost")) {
      const costValue = prettyNumericRound(row?.ExtCost || 0);
      _row = {
        ..._row,
        ExtCost: row?.AllItemsFromFillIn
          ? "-"
          : `$${getFormattedNumber(costValue)}`,
      };
    }
    if (fields.includes("extCost") && row?.IsFromFillIn) {
      _row = {
        ..._row,
        extCost: "-",
      };
    }
    if (fields.includes("ExtendedCost")) {
      const costValue = prettyNumericRound(row?.ExtendedCost || 0);
      _row = {
        ..._row,
        ExtendedCost: row?.AllItemsFromFillIn
          ? "-"
          : `$${getFormattedNumber(costValue)}`,
      };
    }
    if (fields.includes("Selected")) {
      _row = {
        ..._row,
        Selected: row?.AllItemsFromFillIn ? "-" : row?.Selected,
      };
    }
    if (fields.includes("HazmatInd")) {
      _row = {
        ..._row,
        HazmatInd: row?.HazmatInd ? "Yes" : "No",
      };
    }
    if (fields.includes("RestrictedInd")) {
      _row = {
        ..._row,
        RestrictedInd: row?.RestrictedInd ? "Yes" : "No",
      };
    }
    if (fields.includes("Region")) {
      _row = {
        ..._row,
        Region: row?.Region || "-",
      };
    }
    if (fields.includes("Urbanicity")) {
      _row = {
        ..._row,
        Urbanicity: row?.Urbanicity || "-",
      };
    }
    if (fields.includes("Core")) {
      _row = {
        ..._row,
        Core: isXDept ? "No" : !!row?.Core ? row?.Core : "-",
      };
    }
    if (fields.includes("Qty")) {
      _row = {
        ..._row,
        Qty: getFormattedNumber(row?.Qty || 0),
      };
    }
    if (fields.includes("MissingSkus")) {
      _row = {
        ..._row,
        MissingSkus: getFormattedNumber(row?.MissingSkus || 0),
      };
    }
    if (fields.includes("QtyMultiplier")) {
      const multiplierVal = row?.QtyMultiplier
        ? `${row?.QtyMultiplier}x`
        : "1x";
      _row = {
        ..._row,
        QtyMultiplier: row?.AllItemsFromFillIn ? "-" : multiplierVal,
      };
    }
    if (fields.includes("AdjQty")) {
      _row = {
        ..._row,
        AdjQty: row?.AllItemsFromFillIn
          ? "-"
          : getFormattedNumber(row?.AdjQty || 0),
      };
    }
    if (fields.includes("adjQty") && row?.IsFromFillIn) {
      _row = {
        ..._row,
        adjQty: "-",
      };
    }
    if (fields.includes("Cost")) {
      const costValue = prettyNumericRound(row?.Cost || 0);
      _row = {
        ..._row,
        Cost: `$${getFormattedNumber(costValue)}`,
      };
    }
    if (fields.includes("DiscMultiplier")) {
      const disMultiplierVal = row?.DiscMultiplier
        ? `${row?.DiscMultiplier}x`
        : "1x";
      _row = {
        ..._row,
        DiscMultiplier: row?.AllItemsFromFillIn ? "-" : disMultiplierVal,
      };
    }
    if (fields.includes("CoreDiscount")) {
      _row = {
        ..._row,
        CoreDiscount:
          !row?.AllItemsFromFillIn && !isNull(row?.CoreDiscount)
            ? `${row?.CoreDiscount}%`
            : "-",
      };
    }
    if (fields.includes("NonCoreDiscount")) {
      _row = {
        ..._row,
        NonCoreDiscount:
          !row?.AllItemsFromFillIn && !isNull(row?.NonCoreDiscount)
            ? `${row?.NonCoreDiscount}%`
            : "-",
      };
    }
    if (fields.includes("DatingDays")) {
      _row = {
        ..._row,
        DatingDays:
          !row?.AllItemsFromFillIn && !isNull(row?.DatingDays)
            ? row?.DatingDays
            : "-",
      };
    }
    if (fields.includes("plannerNotes") && row?.IsFromFillIn) {
      _row = {
        ..._row,
        plannerNotes: "-",
      };
    }
    if (fields.includes("PlannerNotes")) {
      _row = {
        ..._row,
        PlannerNotes: row?.AllItemsFromFillIn
          ? "-"
          : getNoteValue(plannerNotes, row?.PlannerNotes),
      };
    }
    if (fields.includes("RetailerNotes")) {
      _row = {
        ..._row,
        RetailerNotes: row?.AllItemsFromFillIn
          ? "-"
          : getNoteValue(retailerNotes, row?.RetailerNotes),
      };
    }
    if (fields.includes("retailerNotes") && row?.IsFromFillIn) {
      _row = {
        ..._row,
        retailerNotes: "-",
      };
    }
    if (fields.includes("PONumber")) {
      _row = {
        ..._row,
        PONumber:
          !row?.AllItemsFromFillIn && !!row?.PONumber.trim()
            ? row?.PONumber
            : "-",
      };
    }
    if (fields.includes("ReleaseDate")) {
      const releaseDateValue =
        !row?.AllItemsFromFillIn && !!row?.ReleaseDate
          ? dayjs(row?.ReleaseDate).format("MM/DD/YYYY")
          : "-";
      _row = {
        ..._row,
        ReleaseDate: releaseDateValue,
      };
    }
    if (fields.includes("ReleaseStatus")) {
      _row = {
        ..._row,
        ReleaseStatus: row?.ReleaseStatus || "-",
      };
    }
    if (fields.includes("AllItemsFromFillIn")) {
      _row = {
        ..._row,
        AllItemsFromFillIn: row?.AllItemsFromFillIn ? "Y" : "-",
      };
    }
    if (fields.includes("IsFromFillIn")) {
      _row = {
        ..._row,
        IsFromFillIn: row?.IsFromFillIn ? "Y" : "-",
      };
    }
    if (fields.includes("Restricted")) {
      _row = {
        ..._row,
        Restricted: row?.Restricted === singleValue.yes ? "Yes" : "No",
      };
    }
    if (fields.includes("selected") && row?.IsFromFillIn) {
      _row = {
        ..._row,
        selected: "-",
      };
    }
    if (fields.includes("Hazmat")) {
      _row = {
        ..._row,
        Hazmat: row?.Hazmat === singleValue.yes ? "Yes" : "No",
      };
    }
    if (fields.includes("Model")) {
      _row = {
        ..._row,
        Model: !!row?.Model?.trim() ? row?.Model : "-",
      };
    }
    if (fields.includes("Discontinued")) {
      let discontinuedValue = "No";
      if (Boolean(row?.DiscInd)) {
        if (row?.FutureDiscDate) {
          discontinuedValue = `Yes [Date: ${dayjs(row?.FutureDiscDate).format(
            "MM/DD/YYYY"
          )}]`;
        } else {
          discontinuedValue = "Yes";
        }
      } else {
        if (row?.FutureDiscDate) {
          discontinuedValue = `No [Date: ${dayjs(row?.FutureDiscDate).format(
            "MM/DD/YYYY"
          )}]`;
        } else if (row?.DiscDate) {
          discontinuedValue = `No [Date: ${dayjs(row?.DiscDate).format(
            "MM/DD/YYYY"
          )}]`;
        }
      }
      _row = {
        ..._row,
        Discontinued: discontinuedValue,
        Sub: Boolean(row?.DiscInd)
          ? row?.SubType === SUB_TYPE.sub
            ? row?.SubsItemNbr
            : "-"
          : "-",
        Ref: Boolean(row?.DiscInd)
          ? row?.SubType === SUB_TYPE.ref
            ? row?.SubsItemNbr
            : "-"
          : "-",
      };
    }
    if (fields.includes("UPC")) {
      _row = {
        ..._row,
        UPC: `${padStart(row?.UPC, 14, "0")}`,
      };
    }
    if (fields.includes("RdcQty")) {
      _row = {
        ..._row,
        RdcQty: getFormattedNumber(row?.RdcQty || 0),
      };
    }
    if (fields.includes("RecQty")) {
      _row = {
        ..._row,
        RecQty: isXDept ? "-" : getFormattedNumber(row?.RecQty || 0),
      };
    }
    if (fields.includes("IsMissing")) {
      _row = {
        ..._row,
        IsMissing: row?.IsMissing ? "Yes" : "No",
      };
    }
    if (fields.includes("Tier")) {
      _row = {
        ..._row,
        Tier: !!row?.Tier ? `T${row?.Tier}` : "-",
      };
    }
    if (fields.includes("PogCost")) {
      const costValue = prettyNumericRound(row?.PogCost || 0);
      _row = {
        ..._row,
        PogCost: `$${getFormattedNumber(costValue)}`,
      };
    }

    //Store Supplies
    if (["ssItem", "ssPlanogram"].includes(type)) {
      if (fields.includes("Type")) {
        _row = {
          ..._row,
          Type: row?.Type !== -1 ? storeSuppliesItemMap[row?.Type] : "-",
        };
      }
      if (fields.includes("EachItemCost")) {
        const costValue = prettyNumericRound(row?.EachItemCost || 0);
        _row = {
          ..._row,
          EachItemCost: `$${getFormattedNumber(costValue)}`,
        };
      }
      if (fields.includes("AcqCost")) {
        const costValue = prettyNumericRound(row?.AcqCost || 0);
        _row = {
          ..._row,
          AcqCost: `$${getFormattedNumber(costValue)}`,
        };
      }
      if (fields.includes("OSOSelected")) {
        _row = {
          ..._row,
          OSOSelected: Boolean(row?.OSOSelected) ? "Yes" : "No",
        };
      }
      if (fields.includes("IsFree")) {
        _row = {
          ..._row,
          IsFree: Boolean(row?.IsFree) ? "Free" : "Non Free",
        };
      }
      if (fields.includes("ReleaseStatus")) {
        let status = row?.ReleaseStatus || "-";
        _row = {
          ..._row,
          ReleaseStatus: status,
        };
      }
      if (fields.includes("AdjCost")) {
        const costValue = prettyNumericRound(row?.AdjCost || 0);
        _row = {
          ..._row,
          AdjCost: isDropShipItem(row)
            ? "-"
            : `$${getFormattedNumber(costValue)}`,
        };
      }
    }

    return _row;
  };

export default getValueMapper;
